import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import logger from "logging/logger";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useI18n } from "i18n";
import { selectUser } from "modules/auth/redux/selectors";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { useHistory } from "react-router";
import { getUserName } from "modules/userProfile";
import { PAGES, usePageTracking } from "modules/tracking";
import useCompany from "../../hooks/useCompany";
import { CompaniesList } from "../CompaniesList";
import { CURRENT_COMPANY_ID } from "../../constants";
import { selectCompanies } from "../../redux/selectors";
import { IUserCompany } from "../../types";
import { styles } from "./styles";

export const SelectCompanyPage = () => {
  const history = useHistory();
  const { t } = useI18n(["common", "menu"]);
  const user = useSelector(selectUser);
  const { loadCompany } = useCompany();
  const [isLoading, setIsLoading] = useState(false);
  const userCompanies: IUserCompany[] = useSelector(selectCompanies);
  const { redirect } = useQueryParams();
  const redirectPath = (redirect as string) || "/my-events";

  usePageTracking(PAGES.SELECT_COMPANY);

  useEffect(() => {
    const companyId = localStorage.getItem(CURRENT_COMPANY_ID);

    if (companyId) {
      const userCompany = userCompanies.filter(
        ({ company }) => company.id === companyId,
      );

      if (!userCompany) {
        localStorage.removeItem(CURRENT_COMPANY_ID);

        return;
      }

      // eslint-disable-next-line promise/catch-or-return, promise/always-return, promise/prefer-await-to-then
      loadCompany().then(() => {
        history.push(redirectPath);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanySelect = async (companyId: string | undefined) => {
    if (!companyId) {
      logger.warn("[handleCompanySelect] invalid company id");

      return;
    }
    try {
      setIsLoading(true);
      localStorage.setItem(CURRENT_COMPANY_ID, companyId);
      await loadCompany();
      history.push(redirectPath);
    } catch (err) {
      logger.error(`[handleCompanySelect] unable to load company ${companyId}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box>
        <Typography variant="h2" sx={styles.msg}>{`${t(
          "menu:welcome.back",
        )}, ${getUserName(user)}!`}</Typography>
        <Typography sx={styles.desc}>
          {t("menu:select.your.company")}
        </Typography>
        <CompaniesList handleClick={handleCompanySelect} embedded />
        {isLoading && <PageLoader message={t("loading")} />}
      </Box>
    </Box>
  );
};
